import { takeLatest, call, put, all, select } from '@redux-saga/core/effects';
import Auth from '@aws-amplify/auth';
import Logger from '@noths/logger/build/client';
import MarketingPreferencesService from '../../../services/MarketingPreferencesService';
import Types from '../../types';
import { emailPreference, directMailPreference } from '../../utils/selectors';
import getErrorMessage from '../../utils/getErrorMessage';
import { trackMarketingPreferencesUpdate } from '../../../analytics';

export function* updatePreferences() {
  try {
    const email = yield select(emailPreference);
    const directMail = yield select(directMailPreference);
    const source = 'SETTINGS';

    const preferencesToUpdate = [{ type: 'email', ...email }, { type: 'directMail', ...directMail }]
      .filter(preference => preference.hasChanged)
      .map(({ type, currentStatus, value }) =>
        call(MarketingPreferencesService.post, type, currentStatus, value, source)
      );

    yield all(preferencesToUpdate);
    yield call(trackMarketingPreferencesUpdate, email, directMail);
    yield put({ type: Types.UPDATE_PREFERENCES_SUCCESS });

    if (email.hasChanged && !email.currentStatus) {
      yield put({ type: Types.SHOW_UNSUB_NOTIFICATION });
    } else {
      yield put({ type: Types.HIDE_UNSUB_NOTIFICATION });
    }
  } catch (error) {
    Logger.error(getErrorMessage(error), { extra: { description: 'Update Preferences' } });
    yield put({ type: Types.UPDATE_PREFERENCES_ERROR });
  }
}

export function* getPreferences() {
  try {
    const { attributes } = yield call([Auth, 'currentUserInfo']);
    const address = attributes.email;
    const [email, directMail] = yield all([
      call(MarketingPreferencesService.get, { channel: 'email', address }),
      call(MarketingPreferencesService.get, { channel: 'directMail', address }),
    ]);

    yield put({
      type: Types.GET_PREFERENCES_SUCCESS,
      email: { ...email, address },
      directMail,
    });
  } catch (error) {
    Logger.error(getErrorMessage(error), { extra: { description: `Get Preferences` } });
    yield put({ type: Types.GET_PREFERENCES_ERROR });
  }
}

export function* createPreferences({ email, directMail, source = 'REGISTER' }) {
  try {
    yield all([
      call(MarketingPreferencesService.post, 'email', email.currentStatus, email.value, source),
      call(
        MarketingPreferencesService.post,
        'directMail',
        directMail.currentStatus,
        directMail.value,
        source
      ),
    ]);
  } catch (error) {
    Logger.error(getErrorMessage(error), { extra: { description: `Create Preferences` } });
    yield put({ type: Types.CREATE_PREFERENCES_ERROR });
  }
}

export function* marketingPreferencesSaga() {
  yield takeLatest(Types.GET_PREFERENCES, getPreferences);
  yield takeLatest(Types.UPDATE_PREFERENCES, updatePreferences);
}

export default marketingPreferencesSaga;
